import html2canvas from "html2canvas";
import moment from "moment";

/**
 * Takes a screenshot using html2canvas
 * @param elementId - The ID of the element to capture
 * @param reportName - Localized report name (e.g., t("report_name"))
 * @param date - Optional datetime for the filename (defaults to current date)
 * @param onBefore - Optional callback to execute before taking the screenshot
 * @param onAfter - Optional callback to execute after taking the screenshot
 */
export async function takeScreenshot(
  elementId: string,
  reportName: string,
  date?: string,
  onBefore?: () => void,
  onAfter?: () => void,
): Promise<void> {
  const input = document.getElementById(elementId);
  if (!input) {
    console.error(`Element with id "${elementId}" not found`);
    return;
  }

  if (onBefore) onBefore();

  // Give the UI a moment to update (show overlay spinner) before starting the heavy screenshot operation
  await new Promise((resolve) => setTimeout(resolve, 50));

  // Find the table container and its parent
  const tableContainer = input.querySelector(".MuiTableContainer-root") as HTMLElement;
  const parentBox = tableContainer?.parentElement as HTMLElement;

  // Store original styles
  const originalStyles = {
    tableStyle: tableContainer?.getAttribute("style") || "",
    parentStyle: parentBox?.getAttribute("style") || "",
  };

  // Force everything to be visible and expanded. The overview tables cap their height so their
  // headers stay sticky while scrolling - that cap has to go too, otherwise the capture is clipped.
  if (tableContainer) {
    tableContainer.style.setProperty("overflow", "visible", "important");
    tableContainer.style.setProperty("max-height", "none", "important");
  }
  if (parentBox) {
    parentBox.style.setProperty("overflow", "visible", "important");
    parentBox.style.setProperty("max-height", "none", "important");
  }

  // Helper to restore original styles
  const restoreStyles = () => {
    if (tableContainer) {
      if (originalStyles.tableStyle) {
        tableContainer.setAttribute("style", originalStyles.tableStyle);
      } else {
        tableContainer.removeAttribute("style");
      }
    }
    if (parentBox) {
      if (originalStyles.parentStyle) {
        parentBox.setAttribute("style", originalStyles.parentStyle);
      } else {
        parentBox.removeAttribute("style");
      }
    }
  };

  // Wait for reflow
  setTimeout(() => {
    html2canvas(input, {
      allowTaint: false,
      useCORS: true,
      scale: 2,
      backgroundColor: "#ffffff",
      width: input.scrollWidth,
      height: input.scrollHeight,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        const dateStr = moment(date || undefined).format("YYYY-MM-DD-HH-mm");
        link.download = `${reportName}_${dateStr}.png`;
        link.href = imgData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        restoreStyles();
        if (onAfter) onAfter();
      })
      .catch((error) => {
        console.error("[html2canvas] Screenshot failed:", error);
        restoreStyles();
        if (onAfter) onAfter();
      });
  }, 200);
}
